import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import Loader from 'src/components/common/loader';
import PageHeading from 'src/components/common/page-heading';
import Button from 'src/components/ui/button';
import BurialPlaceForm from './components/burial-place-form';
import CheckoutInfo from './components/checkout-info';
import PurchaserForm from './components/purchaser-form';
import { ReactComponent as ShareIcon } from 'src/assets/icons/share.svg';

import cartStore from 'src/stores/cart-store';
import globalStore from 'src/stores/global-store';
import paymentStore from 'src/stores/payment-store';
import popupStore from 'src/stores/popup-store';

import useForm from 'src/hooks/use-form';
import useScrollableSections from 'src/hooks/use-scrollable-sections';
import useSluggedNavigate from 'src/hooks/use-slugged-navigate';
import useToggle from 'src/hooks/use-toggle';
import { mapFormFieldsToInfo } from 'src/adapters';
import {
  AppRoute,
  GoogleAnalyticsButton,
  GoogleAnalyticsPage,
  IS_MESHULAM_PAYMENT,
  LOCALES,
  PopupName,
} from 'src/constants';
import {
  callNativeSharing,
  checkYearIsSameOrBefore,
  formatMoney,
  getGoogleAnalyticsEvent,
} from 'src/utils';
import type { IClientInfo, IForm } from 'src/interfaces';

import './checkout-page.scss';

enum ScrollableSection {
  BURIAL_PLACE_FORM = 'burial-place-form',
  PURCHASER_FORM = 'purchaser-form',
}

const CheckoutPage = observer(() => {
  const { cemeteryName, decedent, deceasedYearsLife, isNoSlug } = globalStore;
  const { cartData, totalSum, setClientInfo } = cartStore;
  const { isPaymentLoading } = paymentStore;
  const navigate = useSluggedNavigate();
  const { isActive: isAgreementChecked, toggle: toggleAgreement } = useToggle(true);
  const { setRef, scrollToSection } = useScrollableSections<ScrollableSection>();
  const formattedTotalSum = formatMoney(totalSum);
  const buttonLabel = totalSum > 0 ? LOCALES.GO_TO_PAYMENT : LOCALES.PLACE_ORDER;

  const handleShareButtonClick = async () => {
    const shareData = {
      title: decedent.name,
      url: window.location.href,
    };
    const sharingText = `${decedent.name}${
      deceasedYearsLife ? `, (${deceasedYearsLife})` : ''
    } / ${cemeteryName}`;

    const isNativeSharing = await callNativeSharing({ ...shareData, text: sharingText });
    if (!isNativeSharing) {
      popupStore.showPopup(PopupName.SHARE, shareData);
    }
  };

  const handleFormSubmit = useCallback(
    async (info: IClientInfo) => {
      if (!isAgreementChecked && IS_MESHULAM_PAYMENT) {
        return;
      }

      setClientInfo(info);

      if (cartStore.totalSum > 0) {
        return navigate(AppRoute.PAYMENT_PAGE_ROUTE);
      }

      const { no } = await paymentStore.createOrder(null);

      if (no) {
        paymentStore.setPaidOrderNumber(no);
        navigate(`${AppRoute.PAYMENT_RESULT_PAGE_ROUTE}/${no}`);
      }
    },
    [isAgreementChecked, navigate, setClientInfo]
  );

  const handleFormFailed = useCallback(
    (formFields: IForm) => {
      if (
        isNoSlug &&
        (!formFields.cemetery.isValid ||
          !formFields.decedent.isValid ||
          !formFields.yearOfBirth.isValid ||
          !formFields.yearOfDeath.isValid)
      ) {
        scrollToSection(ScrollableSection.BURIAL_PLACE_FORM);
      } else if (
        !formFields.firstName.isValid ||
        !formFields.lastName.isValid ||
        !formFields.phone.isValid ||
        !formFields.email.isValid
      ) {
        scrollToSection(ScrollableSection.PURCHASER_FORM);
      }
    },
    [isNoSlug, scrollToSection]
  );

  const onValidYears = useMemo(() => {
    const fieldsToValidate = ['yearOfBirth', 'yearOfDeath'];

    const isValid = (formFields: IForm) => {
      const { yearOfBirth, yearOfDeath } = formFields;

      if (yearOfBirth.value && yearOfDeath.value) {
        return checkYearIsSameOrBefore(yearOfBirth.value, yearOfDeath.value);
      } else {
        return true;
      }
    };

    const setError = (formFields: IForm) => {
      return !isValid(formFields)
        ? {
            ...formFields,
            yearOfBirth: { ...formFields.yearOfBirth, isValid: false },
            yearOfDeath: { ...formFields.yearOfDeath, isValid: false },
          }
        : formFields;
    };

    return { fieldsToValidate, isValid, setError, textError: LOCALES.THE_YEAR_OF_DEATH_LATER };
  }, []);

  const { formFields, formError, handleChange, handleSubmit } = useForm<IClientInfo>(
    cartStore.initialFormValues,
    handleFormSubmit,
    handleFormFailed,
    onValidYears
  );

  const handleBackButtonClick = () => {
    setClientInfo(mapFormFieldsToInfo(formFields));
    navigate(AppRoute.MAIN_PAGE_ROUTE);
  };

  return (
    <div className="checkout-page">
      <PageHeading
        title={LOCALES.CHECKOUT}
        decedent={decedent}
        isHideDecedentOnDesktop
        step={2}
        description={LOCALES.CHECK_ORDER_DETAILS}
      />

      <div
        className={classNames(
          'checkout-page__content',
          isNoSlug && 'checkout-page__content_no-slug'
        )}
      >
        {isNoSlug ? (
          <BurialPlaceForm
            ref={setRef(ScrollableSection.BURIAL_PLACE_FORM)}
            formFields={formFields}
            formError={formError}
            onChange={handleChange}
          />
        ) : (
          <CheckoutInfo />
        )}

        <div className="checkout-page__receipt-block checkout-page__receipt-block_service">
          <div className="checkout-page__receipt-title">{LOCALES.AFTERCARE_SERVICES}</div>
          <div className="checkout-page__receipt-content">
            {cartData.map(({ lots }) =>
              lots.map(({ count, price, name, id }) => (
                <div className="checkout-page__receipt-service" key={id}>
                  <span className="checkout-page__receipt-service-count">{count} x</span>
                  <span className="checkout-page__receipt-service-product">{name}</span>
                  <span className="checkout-page__receipt-service-price">
                    {formatMoney(price * count)}
                  </span>
                </div>
              ))
            )}
          </div>
          <div className="checkout-page__total">
            <div className="checkout-page__total-block">
              <span className="checkout-page__total-title">{LOCALES.ORDER_TOTAL}:</span>
              <span className="checkout-page__total-price">{formattedTotalSum}</span>
            </div>
            {!isNoSlug && (
              <Button
                className="checkout-page__share-button"
                icon={<ShareIcon />}
                desktopLabel={LOCALES.SHARE_ORDER}
                label={LOCALES.SHARE}
                onClick={handleShareButtonClick}
                size="normal"
                tag="button"
                theme="ghost"
              />
            )}
          </div>
        </div>

        <div>
          <PurchaserForm
            ref={setRef(ScrollableSection.PURCHASER_FORM)}
            formFields={formFields}
            isAgreementChecked={isAgreementChecked}
            onChange={handleChange}
            toggleAgreement={toggleAgreement}
          />

          <div className="checkout-page__buttons">
            <Button
              analyticsEvent={getGoogleAnalyticsEvent(
                GoogleAnalyticsPage.CHECKOUT,
                GoogleAnalyticsButton.GO_TO_PAYMENT
              )}
              className="checkout-page__button checkout-page__button_primary"
              label={buttonLabel}
              isDisabled={!isAgreementChecked && IS_MESHULAM_PAYMENT}
              onClick={handleSubmit}
              size="oversize"
              theme="filled"
            />
            <Button
              analyticsEvent={getGoogleAnalyticsEvent(
                GoogleAnalyticsPage.CHECKOUT,
                GoogleAnalyticsButton.BACK
              )}
              className="checkout-page__button"
              label={LOCALES.BACK}
              onClick={handleBackButtonClick}
              size="oversize"
              theme="outlined"
            />
          </div>
        </div>
      </div>

      {isPaymentLoading && <Loader className="loader_position_fixed checkout-page__loader" />}
    </div>
  );
});

export default CheckoutPage;
